import React, { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav } from 'react-bootstrap';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import "bootstrap-icons/font/bootstrap-icons.css";
import { setSidebarOpen } from '../redux/slice/displaySlice';
import { persistor } from '../redux/store/store';
import { setCssVariables } from '../logic/cssVariables';
import { identifyClarityUser } from '../clarity';
import StateManager from '../components/stateManager/stateManager';
import EulaGate from '../components/eula/EulaGate';
import headerImage from "../assets/images/MysticTan_Unity_Logo_Black.png";
import logout from "../assets/images/icons/Unity App Art Files_LOG OUT BUTTON.png";
import facebookImg from "../assets/images/icons/newFB.png";
import instaImg from "../assets/images/icons/newInsta.png";

// Routes whose content fills the whole surface (camera / QR / the home page's
// edge-to-edge photo bands) opt out of the centered, padded .app-content
// container. Matched by pathname prefix so /session/capturePhoto is
// distinguished from its sibling session steps. The home page renders at both
// "/" and "/greeting", so "/" is treated as bleed explicitly.
const FULL_BLEED = ["/fullscreenFilter", "/scanner", "/session/capturePhoto", "/greeting"];

const Layout = () => {
  const dispatch = useDispatch();
  const letsGo = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.auth?.user);
  const isSidebarOpen = useSelector((state) => state?.display?.sidebar?.open);
  const { t } = useTranslation("translation", { keyPrefix: "layout" });

  const firstSegment = "/" + location.pathname.split('/')[1];
  const isBleed = location.pathname === "/" || FULL_BLEED.some((p) => location.pathname.startsWith(p));

  const toggleSidebar = () => dispatch(setSidebarOpen(!isSidebarOpen));
  const closeSidebar = () => dispatch(setSidebarOpen(false));
  const handleClick = (callback) => {
    closeSidebar();
    callback();
  };

  const yeetTheData = () => {
    persistor.purge().then(() => {
      letsGo("/");
      window.location.reload();
    }).catch((error) => {
      console.error('Failed to clear persisted data', error);
    });
  };

  // Measure rendered chrome heights (--nav-height etc.) into CSS vars. Re-measure
  // on mount, when the navbar appears/disappears with auth, and on width changes.
  // Height-only resizes (the on-screen keyboard) are ignored on purpose so the
  // layout doesn't reflow while typing.
  const lastWidth = useRef(typeof window !== 'undefined' ? window.innerWidth : 0);
  useEffect(() => {
    setCssVariables();
    const handleResize = () => {
      if (window.innerWidth !== lastWidth.current) {
        lastWidth.current = window.innerWidth;
        setCssVariables();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [user]);

  // Identifies the Clarity session against the logged-in user, both on fresh
  // login and on refresh once redux-persist rehydrates `user`.
  useEffect(() => {
    if (user) {
      identifyClarityUser(user);
    }
  }, [user]);

  // Shared by both the mobile drawer and the persistent rail. The close ("X")
  // link only makes sense inside the drawer.
  const NavContent = ({ drawer = false }) => (
    <Nav variant="" defaultActiveKey={firstSegment} className="flex-column nav-container">
      {drawer && <Nav.Link onClick={() => closeSidebar()}>X</Nav.Link>}
      {user && <>
        <Nav.Link onClick={() => handleClick(() => letsGo("/scanner"))}>{t("nav.new", "Start a New Tanning Session ")}</Nav.Link>
        <Nav.Link onClick={() => handleClick(() => letsGo("/fullscreenFilter"))}>{t("nav.tryon", "Spray Tan Try On")}</Nav.Link>
        {/* AI Skin Analysis hidden from nav for now — still reachable at /skin-analysis */}
        <Nav.Link onClick={() => handleClick(() => letsGo("/quiz"))}>{t("nav.buildtan", "Build a Custom Glow Quiz")}</Nav.Link>
      </>}

      <Nav.Link onClick={() => window.open("https://sunless.com/pages/salon-locator", '_blank', "noopener,noreferrer")}>{t("nav.location", "Find a Salon")}</Nav.Link>

      {user && <>
        <Nav.Link onClick={() => handleClick(() => letsGo("/greeting"))}>{t("nav.guide", "Spray Tan Guide")}</Nav.Link>
        <Nav.Link onClick={() => handleClick(() => letsGo("/history"))}>{t("nav.history", "Session History")}</Nav.Link>
        <Nav.Link onClick={() => handleClick(() => letsGo("/account"))}>{t("nav.account", "Account Settings")}</Nav.Link>
        <Nav.Link active={firstSegment === "/tanProfiles"} onClick={() => handleClick(() => letsGo("/tanProfiles"))}>{t("nav.profiles", "Tan Profiles")}</Nav.Link>
      </>}
    </Nav>
  );

  const IconFooter = () => (
    <div className='nav-icon-buttons'>
      <img onClick={() => window.open("https://www.instagram.com/mystictanofficial/", '_blank', "noopener,noreferrer")} src={instaImg} alt="Instagram" />
      <img onClick={() => window.open("https://www.facebook.com/MysticTan", '_blank', "noopener,noreferrer")} src={facebookImg} alt="Facebook" />
    </div>
  );

  return (
    <div className={`app-shell${user ? '' : ' app-shell--bare'}`}>
      {user ? (
        <EulaGate>
          <StateManager />
          <Navbar className="app-shell__bar" expand={false}>
            <div className='navbarContainer'>
              <Navbar.Toggle className="app-shell__toggle" aria-controls="app-shell-nav" onClick={toggleSidebar}>
                <div className='menuToggle'><span /><span /><span /></div>
              </Navbar.Toggle>
              <Navbar.Brand className="app-shell__brand" onClick={() => letsGo("/")}>
                <img src={headerImage} alt="Mystic Tan Unity" />
              </Navbar.Brand>
              <img className='log-out-img' onClick={() => yeetTheData()} src={logout} alt="Log out" />
            </div>
          </Navbar>

          <aside className="app-shell__nav" id="app-shell-nav">
            <NavContent />
            <IconFooter />
          </aside>

          <Offcanvas className="app-shell__drawer" placement="start" show={isSidebarOpen} onHide={closeSidebar}>
            <Offcanvas.Body>
              <NavContent drawer />
              <IconFooter />
            </Offcanvas.Body>
          </Offcanvas>

          <main className="app-shell__main">
            <div className={`app-content${isBleed ? ' app-content--bleed' : ''}`}>
              <Outlet />
            </div>
          </main>
        </EulaGate>
      ) : (
        <Outlet />
      )}
    </div>
  );
};

export default Layout;
